import React from 'react'
import { getPublicLayout } from '@public/components/PublicLayout'
import { Box } from 'components/Layout'
import {
  H2,
  H3,
  OL,
  LowerLetterOL,
  UpperLetterOL,
  LI,
  Paragraph,
  Strong,
} from 'components/Legal'
import { PureSSRIndicator } from 'components/PureSSRIndicator'
import { SPACE } from 'Theme'
import { prepareGetStaticNavigationProps } from '../utils/prepareGetStaticNavigationProps'

export const TermsOfUse = () => (
  <Box mt={SPACE.PX_80} mb={SPACE.PX_120}>
    <H2>THE EXPERT TERMS OF SERVICE</H2>
    <H3>Last Updated: January 28, 2025</H3>
    <br />
    <Paragraph>
      The Expert, Inc., a Delaware corporation (&ldquo;
      <Strong>The Expert</Strong>,&rdquo; &ldquo;<Strong>we</Strong>” &ldquo;
      <Strong>our</Strong>,&rdquo; or &ldquo;<Strong>us</Strong>
      ,&rdquo;) provides and makes available The Expert Service (as defined in
      Section 1 below) subject to these Terms of Service (hereinafter, these
      &ldquo;<Strong>Terms</Strong>&rdquo;). PLEASE READ THESE TERMS CAREFULLY,
      AS THEY SET FORTH THE LEGALLY BINDING TERMS AND CONDITIONS GOVERNING THE
      ACCESS AND USE OF THE EXPERT SERVICE BETWEEN YOU AND THE EXPERT, INC.
    </Paragraph>
    <Paragraph>
      <Strong>PLEASE NOTE</Strong>: THESE TERMS CONTAIN DISPUTE RESOLUTION
      PROVISIONS (SEE SECTION 16) WHICH, WITH LIMITED EXCEPTION, REQUIRE (1)
      THAT YOU SUBMIT DISPUTES YOU HAVE AGAINST THE EXPERT TO BINDING AND FINAL
      ARBITRATION, AND (2) THAT YOU WAIVE YOUR RIGHT TO BRING OR PARTICIPATE IN
      ANY CLASS, GROUP, OR REPRESENTATIVE ACTION OR PROCEEDING. IN ADDITION,
      PLEASE BE ADVISED THAT THE EXPERT DOES NOT PROVIDE ANY WARRANTIES TO YOU
      AND THESE TERMS LIMIT OUR LIABILITY TO YOU AS SET FORTH IN SECTIONS 8 AND
      13.
    </Paragraph>
    <Paragraph>
      <Strong>ACCEPTANCE OF THESE TERMS</Strong>. By accessing or using The
      Expert Service in any manner, including, but not limited to, visiting or
      browsing the Site and/or Marketplace, or placing orders to purchase any
      Products and/or Services (all as defined in Section 1 below):
    </Paragraph>
    <Paragraph>
      (1) you expressly acknowledge that you have read, understand, and you
      agree to be bound by all the terms and conditions set forth in these
      Terms, including, without limitation, any additional terms, policies or
      agreements specified and/or referenced below or otherwise incorporated
      into these terms,
    </Paragraph>
    <Paragraph>
      (2) you expressly affirm, represent and warrant that you meet all of the
      eligibility requirements set forth in Section 2 of these Terms and that
      you are fully able and competent to enter into the terms, conditions,
      obligations, affirmations, representations, and warranties set forth in
      these Terms, and
    </Paragraph>
    <Paragraph>
      (3) if you use The Expert Service and/or purchase any Products, Services,
      and/or Consultations on behalf of another person, or an entity,
      organization, or company, you represent and warrant that you have the
      authority to bind that person, entity, organization, and/or company to
      these Terms and you agree to be bound by these Terms on behalf of that
      person, entity, organization, and/or company (in which case, &ldquo;
      <Strong>you</Strong>
      &rdquo; and &ldquo;<Strong>your</Strong>
      &rdquo; as used throughout these Terms shall also refer to such other
      person, entity, organization, and/or company).
    </Paragraph>
    <Paragraph>
      <Strong>
        YOU MAY NOT ACCESS OR USE THE EXPERT SERVICE (OR ANY PART THEREOF) IF
        YOU DO NOT AGREE TO THESE TERMS, DO NOT HAVE THE REQUISITE AUTHORITY, OR
        DO NOT MEET THE ELIGIBILITY REQUIREMENTS SET FORTH IN SECTION 2 BELOW.
      </Strong>
    </Paragraph>
    <OL>
      <LI>
        <Strong>DEFINITIONS</Strong>

        <OL>
          <LI>
            &ldquo;<Strong>Consultations</Strong>&rdquo; means interior design
            consultations provided by Designers that may be purchased by users
            of The Expert Service.
          </LI>
          <LI>
            &ldquo;<Strong>Content</Strong>&rdquo; means any and all messages,
            photos, video, audio, images, data, information, text,
            communications, materials, documentation, and/or content.
          </LI>
          <LI>
            &ldquo;<Strong>Delivery Services</Strong>&rdquo; means white glove
            professional delivery and/or installation services provided by The
            Expert&apos;s third party providers for Products purchased through
            the Marketplace and/or from The Expert through a Trade Vendor.
          </LI>
          <LI>
            &ldquo;<Strong>Designers</Strong>&rdquo; means third party expert
            interior designers that users can connect and engage with through
            Consultation purchased in connection with The Expert Service.
          </LI>
          <LI>
            &ldquo;<Strong>Marketplace</Strong>&rdquo; means The Expert&apos;s
            online marketplace platform, available through the Site, which users
            may browse and submit orders to purchase Products and Delivery
            Services.
          </LI>
          <LI>
            &ldquo;<Strong>Products</Strong>&rdquo; means the home goods and
            products that users may purchase through the Marketplace and/or
            through a Trade Vendor.
          </LI>
          <LI>
            &ldquo;<Strong>Service Content</Strong>&rdquo; means collectively,
            The Expert Content, Third-Party Content, and User Submissions made
            available through or in connection with the use of The Expert
            Service.
          </LI>
          <LI>
            &ldquo;<Strong>Services</Strong>&rdquo; means, collectively, the
            Delivery Services, and/or any other services we provide or make
            available to you in connection with your use of The Expert Service,
            including, but not limited to, the Text Service (as defined in
            Section 3.4), and connecting users with Designers and purchasing
            Products on behalf of users through Trade Vendors.
          </LI>
          <LI>
            &ldquo;<Strong>Site</Strong>&rdquo; means The Expert&apos;s website
            located at{' '}
            <a
              href="https://www.theexpert.com"
              target="_blank"
              rel="noreferrer"
            >
              www.theexpert.com
            </a>
            .
          </LI>
          <LI>
            &ldquo;<Strong>The Expert Content</Strong>&rdquo; means the Content
            that is created by or on behalf of The Expert and made available
            through or in connection with The Expert Service.
          </LI>
          <LI>
            &ldquo;<Strong>The Expert Service</Strong>&rdquo; means,
            collectively, the Site, Marketplace, and Services.
          </LI>
          <LI>
            &ldquo;<Strong>Third Party Content</Strong>&rdquo; means Content
            that originates from and/or its owned or provided by individuals,
            persons, or entities other than The Expert which is made available
            through or in connection with The Expert Service and/or
            Consultations, which may include, but is not limited to, User
            Submissions provided by other users than you, Product Information
            (defined in Section 13.2 below), profile and background information
            regarding the Designers and any Content provided by such Designers.
          </LI>
          <LI>
            &ldquo;<Strong>Trade Member</Strong>&rdquo; means an accredited
            design firm that has been approved by The Expert to participate in
            the Trade Program.
          </LI>
          <LI>
            &ldquo;<Strong>Trade Program</Strong>&rdquo; means The Expert’s
            trade program under which The Expert may make available certain
            discounts on Product purchases and/or other benefits to Trade
            Members.
          </LI>
          <LI>
            &ldquo;<Strong>Trade Vendor</Strong>&rdquo; means a third party
            vendor from which The Expert may purchase Products on behalf of
            users through The Expert&apos;s trade and/or reseller accounts with
            such third party vendor.
          </LI>
          <LI>
            &ldquo;<Strong>Trade Vendor Terms</Strong>&rdquo; means the terms
            and conditions of the applicable Trade Vendor with respect to your
            purchase of Products through us from such Trade Vendor, including,
            but not limited to, those pertaining to, shipping, delivery,
            installation, returns and/or refunds policies.
          </LI>
          <LI>
            &ldquo;<Strong>User Submissions</Strong>&rdquo; means any and all
            Content, including, but not limited to, any reviews regarding the
            Products, Services and/or Designers, submitted, transmitted and/or
            otherwise provided or made available by or on behalf of users in
            connection with the use of The Expert Service and/or any
            Consultations
          </LI>
        </OL>
      </LI>
      <LI>
        <Strong>ELIGIBILITY</Strong>. You may generally visit and browse the
        Site and Marketplace if you are at least 13 years of age; however, you
        must be at least 18 years old or the age of majority in your
        jurisdiction to create an account to use The Expert Service (an &ldquo;
        <Strong>Account</Strong>&rdquo;) and to purchase Products, Services
        and/or Consultations. You affirm and represent and warrant that you are
        18 years old or the age of majority in your jurisdiction, and fully able
        and competent to enter into the terms, conditions, obligations,
        affirmations, representations, and warranties set forth in these Terms.
      </LI>
      <LI>
        <Strong>ADDITIONAL TERMS & POLICIES</Strong>
        <OL>
          <LI>
            <Strong>Generally</Strong>. When using particular services or
            materials through or in connection with The Expert Service, you will
            be subject to any posted rules applicable to such services or
            materials that may contain terms and conditions or other operating
            rules, policies and procedures in addition to those in these Terms,
            including without limitation, our{' '}
            <a
              href="https://www.theexpert.com/privacy"
              target="_blank"
              rel="noreferrer"
            >
              Privacy Notice
            </a>
            ,{' '}
            <a
              href="https://www.theexpert.com/blog/purchase-terms-and-conditions"
              target="_blank"
              rel="noreferrer"
            >
              Purchase Terms and Conditions
            </a>
            ,{' '}
            <a
              href="https://www.theexpert.com/blog/marketplace-return-policy"
              target="_blank"
              rel="noreferrer"
            >
              Return Policy
            </a>
            ,{' '}
            <a
              href="https://www.theexpert.com/blog/text-service-terms-and-conditions"
              target="_blank"
              rel="noreferrer"
            >
              Text Service T&Cs
            </a>
            , and{' '}
            <a
              href="https://www.theexpert.com/blog/infringement-policy"
              target="_blank"
              rel="noreferrer"
            >
              Infringement Policy
            </a>{' '}
            (collectively, the &ldquo;
            <Strong>Additional Terms</Strong>&rdquo;). All such Additional
            Terms, including, without limitation, the Additional Terms listed
            below in this Section 3, are hereby incorporated into and made a
            part of these Terms by reference. In the event the provisions of any
            Additional Terms conflict with these Terms, the Additional Terms
            shall control solely with respect to the subject matter covered by
            such Additional Terms.
          </LI>
          <LI>
            <Strong>Privacy Policy</Strong>. If you provide us any personal
            information in connection with your access and/or use of The Expert
            Service, The Expert will process such personal information as
            described in these Terms and our{' '}
            <a
              href="https://www.theexpert.com/privacy"
              target="_blank"
              rel="noreferrer"
            >
              Privacy Notice
            </a>
            . By using The Expert Service, you acknowledge and agree that The
            Expert can process your information in accordance with these Terms
            and as set forth in the{' '}
            <a
              href="https://www.theexpert.com/privacy"
              target="_blank"
              rel="noreferrer"
            >
              Privacy Notice
            </a>
            .
          </LI>
          <LI>
            <Strong>Purchase Terms and Conditions; Return Policy</Strong>. If
            you purchase Products through The Expert Service, your purchase
            shall be subject to our{' '}
            <a
              href="https://www.theexpert.com/blog/purchase-terms-and-conditions"
              target="_blank"
              rel="noreferrer"
            >
              Purchase Terms and Conditions
            </a>{' '}
            and, with respect to Products purchased through the Marketplace, our
            <a
              href="https://www.theexpert.com/blog/marketplace-return-policy"
              target="_blank"
              rel="noreferrer"
            >
              Return Policy
            </a>{' '}
            (collectively, the &ldquo;
            <Strong>Sales Terms</Strong>&rdquo;). Please read the Sales Terms
            carefully prior to each purchase as they describe our terms and
            conditions of sale for Product you purchase through The Expert
            Service, and, with respect to Product purchased through the
            Marketplace, our policies and procedures regarding permitted
            returns. For the avoidance of doubt, returns and/or refunds of
            Product purchased through Trade Vendors are subject to the
            applicable Trade Vendor Terms, and not our Return Policy.
          </LI>
          <LI>
            <Strong>Text Service T&Cs</Strong>. If you opt-in to participate in
            The Expert&apos;s mobile messaging service that delivers certain
            service-related marketing and promotion communications via
            OTT/SMS/MMS text messages sent by us (or others on our behalf) (the
            &ldquo;
            <Strong>Text Service</Strong>&rdquo;), your use of, and
            participation in, the Text Service is governed by these Terms as
            well as the{' '}
            <a
              href="https://www.theexpert.com/blog/text-service-terms-and-conditions"
              target="_blank"
              rel="noreferrer"
            >
              Text Service T&Cs
            </a>{' '}
            (the terms of which shall be deemed incorporated into and made a
            part of these Terms by this reference as if set forth in full). For
            the avoidance of doubt, the Text Service is included in the term
            &ldquo;
            <Strong>Services</Strong>&rdquo; as used throughout these Terms and
            any other applicable Additional Terms.
          </LI>
          <LI>
            <Strong>Infringement Policy</Strong>. If you believe that any
            Products and/or content made available through The Expert Service
            violates your copyright and/or trademark rights, please see The
            Expert&apos;s{' '}
            <a
              href="https://www.theexpert.com/blog/infringement-policy"
              target="_blank"
              rel="noreferrer"
            >
              Infringement Policy
            </a>{' '}
            for instructions on sending us a notice of copyright and/or
            trademark infringement.
          </LI>
          <LI>
            <Strong>Designer Terms</Strong>. If you are a Designer, the Designer
            terms and conditions and/or other agreement entered into by you and
            The Expert sets forth additional terms and conditions with respect
            to your provision of Consultations in connection with The Expert
            Service (the &ldquo;
            <Strong>Designer Terms</Strong>&rdquo;). The Designer Terms shall
            control with respect to the subject matter thereof in the event of a
            conflict between these Terms and such Designer Terms. The Designer
            Terms are included in the term “Additional Terms” as used throughout
            these Terms.
          </LI>
          <LI>
            <Strong>Trade Program Agreement</Strong>. If you apply to
            participate in the Trade Program or are a Trade Member, The
            Expert&apos;s then-current Trade Program terms and conditions sets
            forth additional terms and conditions with respect to your
            participation in the Trade Program (the &ldquo;
            <Strong>Trade Program Agreement</Strong>&rdquo;). The Trade Program
            Agreement shall control with respect to the subject matter thereof
            in the event of a conflict between these Terms and the Trade Program
            Agreement. The Trade Program Agreement is included in the term
            &ldquo;Additional Terms&rdquo; as used throughout these Terms.
          </LI>
        </OL>
      </LI>
      <LI>
        <Strong>MODIFICATIONS TO THESE TERMS</Strong>. The Expert reserves the
        right to update or modify these Terms and/or any Additional Terms at any
        time. Except as stated below with respect to material changes, all
        updates and modifications to these documents will be effective from the
        day they are posted online at{' '}
        <a
          href="https://www.theexpert.com/terms"
          target="_blank"
          rel="noreferrer"
        >
          https://www.theexpert.com/terms
        </a>
        , with respect to these Terms, or the specific webpage for the
        applicable Additional Terms. If we make any material changes to these
        Terms and/or any Additional Terms, we will post a prominent notice of
        the changes on the Site and/or through the user interface of the
        Marketplace. In addition, if we have an email address on file for you,
        we may notify you of these changes by sending a notification to the
        applicable email address. Material changes to these Terms and/or
        Additional Terms (as applicable) will become effective on the date set
        forth in the notice. It is your responsibility to regularly visit and
        review these Terms and the Additional Terms for updates, changes and
        modification. If you do not agree to any updates or modifications to
        these Terms and/or any Additional Terms, simply do not use or access The
        Expert Service or purchase any Products, Services and/or Consultations,
        and, if applicable, terminate your user account. If you continue to
        access or use The Expert Service (or any part thereof) or purchase any
        Products, Services and/or Consultations after the applicable effective
        date of the revised Terms and/or Additional Terms, that will constitute
        your acceptance of the revised Terms.
      </LI>
      <LI>
        <Strong>ACCOUNTS; COMMUNICATIONS; USER SUBMISSIONS</Strong>
        <OL>
          <LI>
            <Strong>Accounts</Strong>. To access and use certain features of The
            Expert Service you may need to create and register an Account. In
            registering an Account, you agree to provide and maintain up to date
            information that is true, accurate, current, up to date, and
            complete. You agree that you will not (a) create an Account using a
            false identity or information, and (b) create an Account or use The
            Expert Service if you have been previously removed or banned by us
            from use of The Expert Service, or any part thereof. The Expert
            reserves the right to limit the number of Accounts that can be
            created from any one computer or mobile device and the number of
            computer or mobile devices that can access an individual Account.
          </LI>
          <LI>
            <Strong>Account Security</Strong>. You understand and agree that you
            are solely responsible for maintaining the confidentiality of and
            protecting your password to your Account. You are solely responsible
            for any activity originating from your Account, regardless of
            whether such activity is authorized by you. You agree to notify us
            immediately of any unauthorized use of your Account.
          </LI>
          <LI>
            <Strong>Electronic Communications</Strong>. By using The Expert
            Service, you consent to receiving electronic communications from The
            Expert, including, notices posted on our Site and/or the
            Marketplace, or sent to you via email and, if you have opted-in to
            our Text Service, via text, text message. These electronic
            communications may include notices about applicable fees and
            charges, transactional information and other information concerning
            or related to your use of The Expert Service and/or purchase of any
            Products, Services and/or Consultations, and are part of your
            relationship with The Expert.{' '}
            <Strong>
              You agree that any notices, agreements, disclosures or other
              communications that we send you electronically will satisfy any
              legal communication requirements, including that such
              communications be in writing
            </Strong>
            .
          </LI>
          <LI>
            <Strong>User Submissions</Strong>. To the extent you post, submit,
            transmit, and/or upload, or otherwise provide any User Submissions,
            you grant The Expert a worldwide, non-exclusive, royalty-free, fully
            paid, transferable right and license (including through the use of
            subcontractors) to copy, reproduce, use, host, store, transfer,
            publicly display, publicly perform, transmit, reproduce, modify (for
            the purpose of formatting for display), and distribute your User
            Submissions, in whole or in part, in connection with your use of The
            Expert Service and/or Consultations, and as reasonably necessary to
            provide The Expert Service to you and other users. The Expert will
            only use User Submissions as provided in these Terms and/or any
            applicable Additional Terms, and the{' '}
            <a
              href="https://www.theexpert.com/privacy"
              target="_blank"
              rel="noreferrer"
            >
              Privacy Notice
            </a>
            , or as may be required by law.
          </LI>
          <LI>
            <Strong>Public Submissions & Reviews</Strong>. Please be advised
            that, any User Submissions posted to public forums, chats, blogs,
            message boards, or other communication tools made available through
            or in connection with The Expert Service and/or Consultations will
            be considered non-confidential and non-proprietary.{' '}
            <Strong>
              The Expert does not endorse any User Submissions or any opinion,
              recommendation, or advice expressed therein and takes no
              responsibility for User Submissions. The Expert reserves the
              right, but is not obligated, to monitor User Submissions or other
              content sent to or through The Expert Service. The Expert has the
              right to refuse, remove, edit or delete any User Submissions for
              any reason
            </Strong>
            .
          </LI>
          <LI>
            <Strong>Ownership & Responsibility for User Submissions</Strong>.
            Subject to the licenses you grant us in these Terms, as between The
            Expert and you, you will retain ownership of your User Submissions.
            You acknowledge and agree that you, and not The Expert, are solely
            responsible for any User Submissions submitted, transmitted and/or
            contributed by you, including the legality, reliability, accuracy
            and appropriateness of such User Submissions. By providing User
            Submissions, you represent and warrant that: (a) you own or control
            all rights in and to User Submission, and have the necessary rights
            to grant the licenses granted to The Expert in these Terms; (b) you
            have obtained all permissions and/or approvals as may be necessary
            or required to transmit User Submissions, or any personally
            identifiable information therein, in connection with the use of The
            Expert Service and/or Consultations; and (c) all User Submissions do
            and will comply with these Terms.
          </LI>
        </OL>
      </LI>
      <LI>
        <Strong>USE OF THE EXPERT SERVICE</Strong>
        <OL>
          <LI>
            <Strong>Limited Rights to Access the Site and Marketplace</Strong>.
            Subject to your compliance with these Terms, The Expert hereby
            grants you a personal, non-exclusive, non-transferable,
            non-sublicensable, revocable, limited right to access and use the
            Site and the Marketplace, over the internet, and view the Service
            Content, made available through the Site and the Marketplace, in
            each case on a non-commercial basis, and otherwise in accordance
            with these Terms.
          </LI>
          <LI>
            <Strong>Prohibited Use</Strong>. By using The Expert Service, you
            agree that you will not, and will not permit any other person to:
            <LowerLetterOL>
              <LI>
                Modify, adapt, translate or create derivative works based on The
                Expert Service (or any part thereof) or any Service Content
                (other than your User Submissions);
              </LI>
              <LI>
                Reverse engineer, decompile, disassemble, or otherwise derive or
                determine or attempt to derive or determine the source code (or
                the underlying ideas, algorithms, structure or organization) of
                the Site and/or Marketplace, except as expressly permitted by
                applicable law;
              </LI>
              <LI>
                Distribute, license, sublicense, assign, transfer or otherwise
                make available to any third party The Expert Service or any
                Service Content (other than your User Submissions);
              </LI>
              <LI>
                Remove, alter, or obscure in any way any proprietary rights
                notices (including copyright notices) of The Expert or its
                suppliers on or within The Expert Service or any Service
                Content;
              </LI>
              <LI>
                Store, transmit or upload any material and/or content through or
                in connection with the use of The Expert Service, or engage in
                any behavior in connection with your use of The Expert Service
                and/or any Consultations, that is illegal, abusive, harassing,
                harmful to reputation, pornographic, indecent, profane, obscene,
                hateful, racist, infringing, libelous, tortious, or otherwise
                objectionable in The Expert&apos;s reasonable opinion;
              </LI>
              <LI>
                Use The Expert Service to store or transmit malicious code or
                any material in a way that violates or infringes upon the rights
                of a third party, including those pertaining to: contract,
                intellectual property, privacy, or publicity;
              </LI>
              <LI>
                Use The Expert Service (or any part thereof) and/or any Service
                Content to hack, spam, or phish The Expert, Designers, and/or
                any other users;
              </LI>
              <LI>
                Interfere with or disrupt the integrity or performance of The
                Expert Service (or any part thereof) or any system, network or
                data, or take any action that imposes, or may impose, in our
                sole discretion, an unreasonable or disproportionately large
                load on the Expert&apos;s technology infrastructure or otherwise
                make excessive traffic demands of The Expert Service;
              </LI>
              <LI>
                Attempt to gain unauthorized access to The Expert Service (or
                any part thereof) or its related systems or networks, including,
                without limitation, bypassing any “captcha” requirements or
                similar precautions;
              </LI>
              <LI>
                Remove, circumvent, disable, damage or otherwise interfere with
                any security-related features of The Expert Service, features
                that prevent or restrict the use or copying of any Service
                Content, or features that enforce limitations on the use of The
                Expert Service;
              </LI>
              <LI>
                Frame or utilize framing techniques to enclose The Expert
                Service, Service Content, or any portion thereof;
              </LI>
              <LI>
                Use any meta tags, &ldquo;hidden text&rdquo;, robots, spiders,
                crawlers, or other tools, whether manual or automated, to
                collect, scrape, index, mine, republish, redistribute, transmit,
                sell, license or download The Expert Service, Service Content
                (except caching or as necessary to view the Service Content), or
                the personal information of others without our prior written
                permission or authorization;
              </LI>
              <LI>
                Impersonate any person or entity, or falsely state or otherwise
                misrepresent your affiliation with any person or entity;
              </LI>
              <LI>
                Operate to defraud The Expert, Designers, other users, or any
                other person or provide false, inaccurate or misleading
                information;
              </LI>
              <LI>
                Access and/or use The Expert Service (or any part thereof),
                Service Content, and/or any Consultations for the purpose of
                developing competitive products or services;
              </LI>
              <LI>
                Use The Expert Service, Service Content, or provide any User
                Submissions, other than in accordance with these Terms and/or
                any Additional Terms (if applicable); or
              </LI>
              <LI>
                Use The Expert Service and/or any Service Content in violation
                of any applicable local, state, national or international law,
                including, without limitation, any and all applicable export
                laws.
              </LI>
            </LowerLetterOL>
          </LI>
          <LI>
            <Strong>Feedback</Strong>. You acknowledge that any ideas,
            inventions, suggestions for improvement or discussions submitted by
            you regarding any aspect of The Expert Service, Service Content (but
            excluding your User Submissions), Designers, Trade Vendors,
            Products, Services and/or Consultations, including, without
            limitation, the functioning, features, and other characteristics of
            the Site, Marketplace, Services, and/or Service Content (or any
            component thereof) and any reviews you post regarding the Products,
            Services, Designers, Consultations and/or Trade Vendors (&ldquo;
            <Strong>Feedback</Strong>&rdquo;) may be used by The Expert without
            compensation or attribution to you, and you hereby grant The Expert,
            its subsidiaries, affiliates and partners a worldwide, irrevocable,
            royalty free, non-exclusive, sublicensable and transferable license
            under all your intellectual property rights in and to such Feedback,
            for The Expert to use and exploit for any purpose.
          </LI>
          <LI>
            <Strong>Proprietary Rights</Strong>. As between you and The Expert,
            The Expert and its licensors retain all right, title and interest in
            and to The Expert Content and The Expert Service, including, but not
            limited to, the visual interfaces, interactive features, graphics,
            design, compilation, our selection, coordination, aggregation, and
            arrangement of Service Content, computer code, products, software,
            aggregate star ratings, and all other elements and components of The
            Expert Service (but excluding any User Submission and Third Party
            Content), and any and all modifications, enhancements and updates to
            any of the foregoing. All The Expert trademarks, tradenames, service
            marks, logos and branding are strictly owned by The Expert, and
            nothing in these Terms will be construed to transfer ownership
            rights or grant any permission, license or other rights to any
            trademark, tradename, service mark, logo and/or branding of The
            Expert without written authorization from The Expert. The
            trademarks, service marks, logos, and/or names of individuals,
            companies and/or products mentioned through The Expert Service
            and/or Service Content may be the trademarks of their respective
            owners. The Expert reserves all rights and licenses not expressly
            granted to you in these Terms and no implied license is granted by
            The Expert. The Expert Service (and its underlying technology) and
            The Expert Content are protected by copyright, trademark, patent,
            intellectual property, and other laws of the United States and
            foreign countries.
          </LI>
        </OL>
      </LI>
      <LI>
        <Strong>CONSULTATIONS FROM DESIGNERS</Strong>. The terms and conditions
        set forth in this Section 7 shall apply to you if you engage with and/or
        purchase any Consultations from a Designer through The Expert Service.
        <OL>
          <LI>
            <Strong>Consultations</Strong>. The Expert Service enables users to
            connect with Designers and purchase Consultations from such
            Designers. If you decide to engage a Designer you acknowledge and
            agree that you will be required to and shall pay the applicable fees
            for the Consultation purchased by you specified in the Order
            submitted by you to engage such Designer (the &ldquo;
            <Strong>Consultation Fees</Strong>&rdquo;). The Consultation Fees
            shall be paid through the payment processing methods available
            through The Expert Service in accordance with and as set forth in
            Section 9 below. You agree that you will not indirectly or directly
            offer or solicit any Designers, or accept any offer or solicitation
            from a Designer, to invoice, pay, or receive payment of the
            Consultation Fees or any other fees and/or amounts in any manner
            other than through The Expert Service.
          </LI>
          <LI>
            <Strong>Consultation Cancellations and Rescheduling</Strong>.
            Subject to these Terms, you may cancel or reschedule a Consultation
            up to 72 hours before the scheduled time of the Consultation for a
            full refund.{' '}
            <Strong>
              If you cancel or request a reschedule less than 72 hours before
              your Consultation, you will be charged in full
            </Strong>
            . In addition, you are allowed one cancellation and one reschedule
            between each completed Consultation.{' '}
            <Strong>
              Any cancellations and/or reschedules beyond that will politely be
              declined and charged in full
            </Strong>
            .
          </LI>
        </OL>
      </LI>
      <LI>
        <Strong>YOUR DEALINGS WITH DESIGNERS AND/OR TRADE VENDORS</Strong>
        <OL>
          <LI>
            <Strong>Identity Disclaimer</Strong>. Please note that The Expert
            does not currently conduct any verification of or background checks
            on any Designers and/or Trade Vendors. The Expert does not
            represent, warrant or guarantee the identity of any Designers and/or
            Trade Vendors and whether any Designers and/or Trade Vendors are
            trustworthy. You are solely responsible for making your own
            decisions about the suitability of Designers and/or Trade Vendors
            whom you contact, communicate, transact or interact with in
            connection with your use of The Expert Service, including, but not
            limited to, any Consultations and/or Trade Vendor Products purchased
            by you.
          </LI>
          <LI>
            <Strong>Your Dealings with Designers and/or Trade Vendors</Strong>.
            You acknowledge, understand, and agree that, except solely with
            respect to The Expert&apos;s payment processing and/or order
            placement obligations (as applicable) with respect to the fees paid
            by you through The Expert Service for Consultations and/or Products
            from Trade Vendors purchased by you, any agreement(s),
            understandings, interactions, communications, and/or dealings
            between you and any Designer and/or Trade Vendor, including, but not
            limited to, the Consultations with respect to Designers and/or any
            Products purchased by you from Trade Vendors and the applicable
            Trade Vendor Terms (all of the foregoing, collectively, &ldquo;
            <Strong>Dealings</Strong>&rdquo;), shall be solely between you and
            the applicable Designer and/or the Trade Vendor, and The Expert
            shall not be a party to any Dealings, or have any responsibility or
            liability for such Dealings and/or the performance of the applicable
            Designer and/or Trade Vendor of its obligations under or in
            connection with any such Dealings. You further acknowledge,
            understand and agree that any problems or disputes between you and
            any Designer and/or Trade Vendor and/or with respect to any Dealings
            between you and any Designer and/or Trade Vendor (collectively,
            &ldquo;
            <Strong>Your Disputes</Strong>&rdquo;) must be resolved solely
            between you and such Designer and/or Trade Vendor. NOTWITHSTANDING
            ANYTHING TO THE CONTRARY HEREIN, THE EXPERT DOES NOT WARRANT,
            ENDORSE, GUARANTEE OR ASSUME RESPONSIBILITY FOR ANY DESIGNER AND/OR
            TRADE VENDOR AND/OR THE PERFORMANCE OF ANY SERVICES (INCLUDING, BUT
            NOT LIMITED TO CONSULTATIONS) BY SUCH DESIGNERS OR THE SERVICES
            AND/OR PRODUCTS OF SUCH TRADE VENDORS, OR IN ANY WAY MONITOR, ANY
            TRANSACTION, ENGAGEMENT AND/OR RELATIONSHIP BETWEEN YOU AND ANY
            DESIGNER AND/OR TRADE VENDOR. YOUR USE OF AND/OR ENGAGEMENT WITH ANY
            DESIGNER AND/OR PURCHASE OF ANY PRODUCT FROM A TRADE VENDOR SHALL BE
            AT YOUR SOLE AND ENTIRE RISK. THE EXPERT IS NOT AND SHALL NOT BE
            RESPONSIBLE FOR ANY LOSS, HARM OR DAMAGE OF ANY SORT INCURRED AS A
            RESULT OF ANY SUCH DEALINGS OR OTHERWISE ARISING FROM YOUR DISPUTES
            AND YOU AGREE THAT THE EXPERT IS UNDER AND HAS NO OBLIGATION TO
            BECOME INVOLVED IN ANY OF YOUR DISPUTES.
          </LI>
          <LI>
            <Strong>Release</Strong>. YOU, ON BEHALF OF YOURSELF AND YOUR
            SUCCESSORS, ASSIGNS, REPRESENTATIVES, EMPLOYEES, AGENTS, CLIENTS,
            AND ANYONE ELSE CLAIMING BY OR THROUGH YOU, HEREBY EXPRESSLY
            RELEASE, DISCHARGE, AND HOLD THE EXPERT AND ITS EMPLOYEES, AGENTS,
            REPRESENTATIVES, SHAREHOLDERS, OFFICERS, DIRECTORS, AFFILIATES,
            CONTRACTORS, LICENSORS, SUPPLIERS, SUCCESSORS AND ASSIGNEES
            (COLLECTIVELY, THE &ldquo;
            <Strong>RELEASED PARTIES</Strong>&rdquo;) HARMLESS FROM, AND WAIVE
            ANY AND ALL CLAIMS (INCLUDING BUT NOT LIMITED TO CLAIMS FOR PROPERTY
            DAMAGE, PERSONAL INJURY, OR DEATH), DEMANDS, AND DAMAGES OF EVERY
            KIND OR NATURE, KNOWN OR UNKNOWN, SUSPECTED OR UNSUSPECTED,
            DISCLOSED OR UNDISCLOSED, ARISING OUT OF OR IN ANY WAY RELATED TO
            CONSULTATIONS AND/OR TRADE VENDOR PRODUCTS PURCHASES BY YOU AND/OR
            YOUR DEALINGS AND/OR YOUR DISPUTES WITH ANY DESIGNERS AND/OR TRADE
            VENDORS. IF YOU ARE A CALIFORNIA RESIDENT, YOU SHALL AND HEREBY
            WAIVE, ON BEHALF OF YOURSELF AND YOUR SUCCESSORS, ASSIGNS,
            REPRESENTATIVES, EMPLOYEES, AGENTS, CLIENTS, AND ANYONE ELSE
            CLAIMING BY OR THROUGH YOU, CALIFORNIA CIVIL CODE SECTION 1542,
            WHICH SAYS: &ldquo;A GENERAL RELEASE DOES NOT EXTEND TO CLAIMS WHICH
            THE RELEASING PARTY DOES NOT KNOW OR SUSPECT TO EXIST IN HIS OR HER
            OR ITS FAVOR AT THE TIME OF EXECUTING THE RELEASE, WHICH, IF KNOWN
            BY HIM, HER OR IT MUST HAVE MATERIALLY AFFECTED HIS, HER OR ITS
            SETTLEMENT WITH THE RELEASED PARTIES.&rdquo;
          </LI>
        </OL>
      </LI>
      <LI>
        <Strong>FEES; PAYMENT TERMS</Strong>
        <OL>
          <LI>
            <Strong>
              Fees for The Expert Service and Consultations; Payment Terms
            </Strong>
            . The fees payable by you in connection to your use of The Expert
            Service and/or the purchase of Consultations shall be set forth in
            the applicable electronic or written order (each, an &ldquo;
            <Strong>Order</Strong>&rdquo;) submitted by you to obtain access to
            The Expert Service and/or purchase the Consultations. You agree to,
            and shall, pay The Expert, and hereby authorize The Expert and its
            third payment processors to charge you, the fees, charges and taxes,
            specified in the Order submitted by you to The Expert for access to
            The Expert Service and/or the purchase of any Consultations, in
            accordance with the payment terms specified therein. You will be
            asked to supply certain information relevant to your Order
            including, without limitation, your credit card number, the
            expiration date of your credit card (and CCV#), your billing
            address, and/or your shipping information. You represent and warrant
            that you have the legal right to use any credit card(s) or other
            payment method(s) provided by you in connection with any Order. By
            submitting such information, you grant The Expert the right to
            provide such information to our third party service providers for
            purposes of facilitating the completion of Orders initiated by you
            or on your behalf. Verification of your information may be required
            prior to the acknowledgment or completion of any Order.
          </LI>
          <LI>
            <Strong>Products and Delivery Services</Strong>. The fees and
            payment terms applicable to your purchase of any Products and
            related Delivery Services are set forth in the{' '}
            <a
              href="https://www.theexpert.com/blog/purchase-terms-and-conditions"
              target="_blank"
              rel="noreferrer"
            >
              Purchase Terms and Conditions
            </a>
            .
          </LI>
          <LI>
            <Strong>Gift Cards</Strong>. We may offer the ability to purchase
            gift cards (&ldquo;
            <Strong>Gift Cards</Strong>&rdquo;) for use to purchase
            Consultations and/or Products through the Marketplace. All Gift Card
            purchases are non-refundable. Gift Card purchases are solely
            redeemable for a Consultation or a portion of a Consultation and for
            Products listed on the Marketplace. Gift Cards are non-redeemable
            for Products purchased through Trade Vendors. Only one Gift Card can
            be used per Consultation booking or per Order of Products through
            the Marketplace.
          </LI>
          <LI>
            <Strong>Promotions; Discounts</Strong>. We may from time to time
            offer discounts, credits, offers, or promotions (collectively,
            &ldquo;
            <Strong>Promotions</Strong>&rdquo;) Promotions will be subject to
            the applicable terms and conditions presented with the Promotion
            (the &ldquo;
            <Strong>Promotion Terms</Strong>&rdquo;). Promotions: (a) are
            limited to one use per person, per household, unless the Promotion
            Terms state otherwise and are for your own personal non-commercial
            use, and may not be shared with any other person, (b) are valid for
            full-price items only; does not include clearance, price-matched or
            marked-down items, unless otherwise expressly included as part of
            the Promotion; (c) are not valid for any excluded products,
            services, brands or other exclusions listed in the Promotion Terms;
            (d) are not valid for (and will not be applied against) any taxes
            and shipping fees; (e) cannot be applied to previous purchases or
            the purchase of Gift Cards and cannot be redeemed for cash; (f) are
            only valid while supply lasts; and (g) are void where prohibited. In
            addition, the Promotion Terms are subject to change and the
            Promotion is subject to end at any time, in The Expert&apos;s sole
            discretion, without notice.
          </LI>
          <LI>
            <Strong>Returns; Cancellation</Strong>. Except as otherwise
            expressly set forth in these Terms, the Purchase Terms and
            Conditions, and/or Return Policy, or as required by law,{' '}
            <Strong>
              all purchases of Products, Services and/or Consultations are
              final, non-refundable, and non-cancellable
            </Strong>
            . Refunds, if any, shall be issued back to the original form of
            payment.
          </LI>
          <LI>
            <Strong>Changes to Fees & Payment Terms</Strong>. We may at any time
            and from time to time, in our sole discretion, change the fees,
            pricing models, charges, and/or payment terms, or add new fees,
            pricing models, charges, and/or payment terms applicable to The
            Expert Service, Consultations and/or purchase of Products through
            Trade Vendors.
          </LI>
        </OL>
      </LI>
      <LI>
        <Strong>
          THIRD PARTY INTEGRATIONS, THIRD PARTY CONTENT, SERVICES & LINKED SITES
        </Strong>
        <OL>
          <LI>
            <Strong>Third Party Integrations</Strong>. The Expert Service may
            integrate and/or enable you to utilize certain third-party products,
            services and/or applications (including, without limitation,
            including, without limitation, data products and services) in
            connection with the use of The Expert Service which are not owned,
            controlled, or operated by The Expert and are subject to separate
            terms and conditions (collectively, &ldquo;
            <Strong>Third-Party Integrations</Strong>&rdquo;). For example, we
            may integrate with single sign-on services, which enable you to log
            into your Account using your sign-on credentials through a third
            party account (such as, Google), and the Designers use Zoom video
            conferencing to provide Consultations, each of which are a Third
            Party Integration. Your use of any Third-Party Integrations is
            governed by the terms and conditions, terms of service, and/or
            similar end user agreements of the respective service providers. The
            Third-Party Integrations are not part of or included in the term
            “The Expert Service” as used through these Terms, and are operated
            and controlled by the applicable third-party providers of the
            Third-Party Integrations. YOU ARE SOLELY RESPONSIBLE FOR REVIEWING
            AND FOR COMPLYING WITH THE APPLICABLE TERMS OF SERVICE AND PRIVACY
            NOTICES FOR SUCH THIRD-PARTY INTEGRATIONS. FAILURE TO COMPLY MAY
            RESULT IN THE TERMINATION OF YOUR ACCOUNT WITH A THIRD-PARTY
            INTEGRATION PROVIDER AND INABILITY TO USE A PARTICULAR FEATURE OR
            SERVICE WE OFFER.
          </LI>
          <LI>
            <Strong>Third Party Content, & Services & Linked Sites</Strong>. We
            may publish and/or make available Third Party Content on or through
            The Expert Service and/or links or advertisements to certain
            third-party websites, products and/or services (collectively,
            &ldquo;
            <Strong>Linked Sites & Ads</Strong>&rdquo;). The Expert may receive
            payment or a commission if you purchase any services, products,
            applications, and/or goods offered by or through the providers of
            such Linked Sites & Ads (collectively, the &ldquo;
            <Strong>Third-Party Integrations</Strong>&rdquo;); however, the
            Linked Sites & Ads and the applicable Third-Party Services are not
            controlled or owned by The Expert. If you decide to use such Linked
            Sites & Ads and/or any Third-Party Services, be advised that your
            use is governed solely by the terms and conditions of the respective
            providers of such Linked Sites & Ads and/or the Third-Party
            Services. YOU ARE RESPONSIBLE FOR REVIEWING AND COMPLYING WITH THE
            APPLICABLE TERMS AND CONDITIONS AND PRIVACY NOTICES FOR SUCH LINKED
            SITES & ADS AND/OR THIRD-PARTY SERVICES.
          </LI>
        </OL>
      </LI>
      <LI>
        <Strong>INDEMNIFICATION</Strong>. You agree to, and shall indemnify,
        defend and hold harmless The Expert, its employees, contractors, service
        providers, vendors, and/or agents, from and against any and all claims,
        actions, demands, liabilities, losses, damages, expenses, and costs
        (including reasonable attorney fees) arising from (a) your breach of
        these Terms and/or any other Additional Terms, (b) violation of any
        license or other agreement applicable to any Third Party Integrations;
        (c) failure to perform and/or comply with your obligations and
        responsibilities applicable to any Consultations purchased by you, (d)
        your use or alleged misuse of The Expert Service (or any part thereof)
        and/or any Service Content, (e) your use and/or misuse of the
        Consultations you purchase, (f) your User Submissions; and/or (g) any of
        your Dealings and Your Disputes. We reserve the right, at our own
        expense, to assume the exclusive defense and control of any matter
        otherwise subject to indemnification by you hereunder, in which case,
        you agree to cooperate with us in the defense of the claim. Some
        jurisdictions limit consumer indemnities, so some portions or all of
        this indemnity provision may not apply to you.
      </LI>
      <LI>
        <Strong>TERMINATION & CHANGES TO THE EXPERT SERVICE</Strong>
        <OL>
          <LI>
            <Strong>Termination</Strong>. These Terms will remain in full force
            and effect as long as you continue to access or use The Expert
            Service (or any part thereof), or until terminated in accordance
            with the provisions of these Terms. You may terminate at any time
            ceasing all use of The Expert Service and, if you have an Account,
            terminating your Account by emailing us at{' '}
            <a href="mailto:hello@theexpert.com">hello@theexpert.com</a>. At any
            time, The Expert may suspend or terminate your rights to access or
            use The Expert Service (or any part thereof), and/or terminate these
            Terms with respect to you, if The Expert, in good faith, believes
            that you have used The Expert Service (or any part thereof) in
            violation of these Terms and/or any Additional Terms. Upon
            termination of these Terms, your Account and your right to use The
            Expert Service will automatically terminate. The provisions of
            Sections 1, 2, 5, 6.2, 6.3, 6.4, 7, 8, 9, 10.2, 13, 15, 16 and 17,
            together with any other Sections and/or provisions in any Additional
            Terms and/or any rights and obligations herein or in any Additional
            Terms which by their nature are reasonably intended to survive such
            termination, will survive any termination of these Terms.
          </LI>
          <LI>
            <Strong>Changes and Modifications</Strong>. The Expert reserves the
            right to either temporarily or permanently modify, suspend, remove,
            or discontinue The Expert Service (in whole or in part) and/or any
            Products and Services offered through or in connection with The
            Expert Service, at any time, with or without notice. You agree that
            The Expert will not be liable to you or to any third party for any
            modification, suspension, removal or discontinuance of The Expert
            Service (or any part thereof) and/or any Products or Services.
          </LI>
        </OL>
      </LI>
      <LI>
        <Strong>
          DISCLAIMERS OF WARRANTIES & LIMITATIONS ON OUR LIABILITY
        </Strong>
        <OL>
          <LI>
            <Strong>Disclaimer of Warranties - Generally</Strong>. THE EXPERT
            SERVICE, SERVICE CONTENT, PRODUCTS (SUBJECT TO THE{' '}
            <a
              href="https://www.theexpert.com/blog/marketplace-return-policy"
              target="_blank"
              rel="noreferrer"
            >
              RETURN POLICY
            </a>{' '}
            WITH RESPECT TO MARKETPLACE PRODUCTS ONLY), CONSULTATIONS, AND/OR
            ANY OTHER MATERIALS PROVIDED BY THE EXPERT, ARE ALL PROVIDED
            &ldquo;AS IS&rdquo; AND &ldquo;AS AVAILABLE&rdquo;. THE EXPERT AND
            ITS LICENSORS AND SUPPLIERS HEREBY EXPRESSLY DISCLAIM ANY
            REPRESENTATIONS, WARRANTIES OR GUARANTEES OF ANY KIND, EXPRESS OR
            IMPLIED, INCLUDING WARRANTIES OF TITLE, QUALITY, MERCHANTABILITY,
            FITNESS FOR ANY PARTICULAR PURPOSE, SATISFACTORY PURPOSE, ACCURACY,
            NON-INFRINGEMENT, AND ANY WARRANTIES AND CONDITIONS THAT MAY ARISE
            FROM COURSE OF DEALING, COURSE OF PERFORMANCE OR USAGE OF TRADE. THE
            EXPERT AND ITS LICENSORS AND SUPPLIERS DO NOT WARRANT OR MAKE ANY
            GUARANTEE THAT DEFECTS WILL BE CORRECTED OR THAT THE EXPERT SERVICE
            (OR ANY PART THEREOF), SERVICE CONTENT, PRODUCTS, CONSULTATIONS, OR
            ANY OTHER MATERIALS PROVIDED BY THE EXPERT OR IN CONNECTION WITH THE
            EXPERT SERVICE WILL MEET YOUR REQUIREMENTS; (B) WILL BE COMPATIBLE
            WITH YOUR HOME NETWORK, COMPUTER OR MOBILE DEVICE, OR ANY THIRD
            PARTY PRODUCTS OR SERVICES, INCLUDING, BUT NOT LIMITED TO, THIRD
            PARTY INTEGRATIONS; (C) WILL BE AVAILABLE ON AN UNINTERRUPTED,
            TIMELY, SECURE OR ERROR-FREE BASIS; OR (D) WILL BE ACCURATE OR
            RELIABLE. NO ADVICE OR INFORMATION, WHETHER ORAL OR WRITTEN,
            OBTAINED BY YOU FROM THE EXPERT, ANY THIRD PARTY, INCLUDING, BUT NOT
            LIMITED TO, ANY DESIGNERS AND/OR TRADE VENDORS, OR THROUGH THE
            EXPERT SERVICE ANY SERVICE CONTENT, AND/OR CONSULTATIONS, SHALL
            CREATE ANY WARRANTY.
          </LI>
          <LI>
            <Strong>
              Disclaimer – Product Information, Colors; Product Listings
            </Strong>
            . ALL INFORMATION PROVIDED AND/OR MADE AVAILABLE THROUGH OR IN
            CONNECTION WITH THE EXPERT SERVICE AND/OR CONSULTATIONS REGARDING
            THE PRODUCTS, INCLUDING WITHOUT LIMITATION, ANY INFORMATION, IMAGES,
            PICTURES AND/OR MATERIALS INCLUDED IN PRODUCTS LISTINGS, AND/OR ANY
            INFORMATION ABOUT THE VENDOR OF THE PRODUCTS (COLLECTIVELY, &ldquo;
            <Strong>Product Information</Strong>&rdquo;) IS PROVIDED FOR
            INFORMATIONAL PURPOSES AND PERSONAL USES ONLY. YOU UNDERSTAND AND
            AGREE THAT THE EXPERT USES COMMERCIALLY REASONABLE EFFORTS TO
            DISPLAY THE COLORS OF THE PRODUCTS ACCURATELY ON THE SITE AND THE
            MARKETPLACE. HOWEVER, BECAUSE INDIVIDUAL COMPUTER MONITORS AND/OR
            DEVICES MAY DISPLAY COLORS DIFFERENTLY, THE EXPERT IS NOT
            RESPONSIBLE FOR THE COLOR ACCURACY OF ANY PRODUCTS DISPLAYED ON OR
            THROUGH THE SITE AND/OR MARKETPLACE, AND DISCLAIMS ALL LIABILITY IN
            THIS REGARD. THE EXPERT ASSUMES NO LIABILITY WHATSOEVER FOR
            INACCURACIES, MISSTATEMENTS, OR OMISSIONS RELATED TO ANY PRODUCT
            INFORMATION, INCLUDING, WITHOUT LIMITATION, COLOR ACCURACY OF ANY
            PRODUCTS DISPLAYED OR LISTED ON THE SITE AND/OR MARKETPLACE. YOUR
            USE OF OR RELIANCE UPON ANY PRODUCT INFORMATION, INCLUDING, WITHOUT
            LIMITATION, ANY IMAGES OF ANY PRODUCTS DISPLAYED ON OR THROUGH THE
            SITE AND/OR MARKETPLACE, IS ENTIRELY AT YOUR OWN RISK, FOR WHICH WE
            WILL NOT BE LIABLE.
          </LI>
          <LI>
            <Strong>
              Disclaimer – Third Party Content, Integrations, Sites, Products
              and Services
            </Strong>
            . THE EXPERT DOES NOT ENDORSE, IS NOT RESPONSIBLE FOR, AND MAKES NO
            REPRESENTATIONS AS TO ANY THIRD- PARTY CONTENT, THIRD PARTY,
            INTEGRATIONS, LINKED SITES AND/OR THIRD-PARTY SERVICES (ALL OF THE
            FOREGOING, &ldquo;
            <Strong>THIRD PARTY PRODUCTS AND/OR SERVICES</Strong>&rdquo;), THEIR
            CONTENT OR THE MANNER IN WHICH THE PROVIDERS OF SUCH THIRD-PARTY
            PRODUCTS AND/OR SERVICES HANDLE YOUR DATA. IN ADDITION, THE EXPERT
            IS NOT AND WILL NOT BE A PARTY TO, OR IN ANY WAY MONITOR, ANY
            TRANSACTION BETWEEN YOU AND ANY THIRD-PARTY PROVIDERS OF ANY THIRD-
            PARTY PRODUCTS AND/OR SERVICES. THE EXPERT IS NOT AND SHALL NOT
            LIABLE FOR ANY DAMAGE OR LOSS CAUSED OR ALLEGED TO BE CAUSED BY OR
            IN CONNECTION WITH YOUR ACCESS OR USE OF ANY THIRD-PARTY PRODUCTS
            AND/OR SERVICES, OR YOUR RELIANCE ON THE PRIVACY PRACTICES OR OTHER
            POLICIES OF THE PROVIDERS OF SUCH THIRD-PARTY PRODUCTS AND/OR
            SERVICES. YOUR USE OF AND RELIANCE ON ANY THIRD-PARTY PRODUCTS
            AND/OR SERVICES IS AT YOUR OWN RISK.
          </LI>
          <LI>
            <Strong>Limitation of Liability</Strong>. TO THE MAXIMUM EXTENT
            PERMITTED BY APPLICABLE LAW, IN NO EVENT SHALL THE EXPERT BE LIABLE
            OR OBLIGATED, WITH RESPECT TO THESE TERMS, AND ANY OTHER ADDITIONAL
            TERMS (INCLUDING THOSE REFERENCED HEREIN), THE EXPERT SERVICE (OR
            ANY PART THEREOF), SERVICE CONTENT, PRODUCTS, CONSULTATIONS, AND ANY
            OTHER MATERIALS PROVIDED BY OR ON BEHALF OF THE EXPERT, WHETHER
            UNDER ANY CONTRACT, NEGLIGENCE, STRICT LIABILITY OR OTHER LEGAL OR
            EQUITABLE THEORY AND EVEN IF THE EXPERT HAS BEEN ADVISED OF THE
            POSSIBILITY OF SUCH LIABILITY OR OBLIGATION:
            <UpperLetterOL>
              <LI>
                IN THE AGGREGATE, FOR ANY AMOUNTS IN EXCESS OF THE GREATER OF
                THE FEES PAID TO THE EXPERT BY YOU UNDER THE APPLICABLE ORDER
                WITH RESPECT TO THE PRODUCTS, SERVICES, AND/OR CONSULTATIONS
                GIVING RISE TO THE CLAIM OR LIABILITY, OR ONE HUNDRED DOLLARS
                ($100.00);
              </LI>
              <LI>
                FOR ANY COST OF PROCUREMENT OF SUBSTITUTE GOODS, TECHNOLOGY,
                SERVICES OR RIGHTS, OR ANY INCIDENTAL, SPECIAL, EXEMPLARY,
                PUNITIVE, RELIANCE, OR CONSEQUENTIAL DAMAGES;
              </LI>
              <LI>FOR INTERRUPTION OF USE OR LOSS OR CORRUPTION OF DATA; OR</LI>
              <LI>
                FOR ANY MATTER BEYOND THE EXPERT&apos;S REASONABLE CONTROL.
              </LI>
            </UpperLetterOL>
            THE PARTIES AGREE THAT THESE LIMITATIONS SHALL APPLY EVEN IF THESE
            TERMS OR ANY LIMITED REMEDY SPECIFIED HEREIN IS FOUND TO HAVE FAILED
            OF ITS ESSENTIAL PURPOSE. THE EXPERT DISCLAIMS ALL LIABILITY OF ANY
            KIND OF THE EXPERT&apos;S LICENSORS AND SUPPLIERS.
          </LI>
          <LI>
            <Strong>
              Application of Limitations and Disclaimers to Consumers
            </Strong>
            . Certain states and/or jurisdictions do not allow the exclusion of
            implied warranties or limitation of liability for incidental or
            consequential damages with respect to consumers (i.e., a person
            acquiring goods otherwise than in the course of a business), so the
            disclaimers, exclusions and limitations set forth in this Section 13
            may not apply to you if you are a consumer. The limitations or
            exclusions of warranties and liability contained in these Terms do
            not affect or prejudice the statutory rights of a consumer. The
            limitations or exclusions of warranties and remedies contained in
            these Terms shall apply to you as a consumer only to the extent such
            limitations or exclusions and remedies are permitted under the laws
            of the jurisdiction where you are located.
          </LI>
          <LI>
            <Strong>Basis of the Bargain</Strong>. The warranty disclaimer and
            limitation of liability set forth above in this Section 13 are
            fundamental elements of the basis of the agreement between The
            Expert and you. The Expert would not be able to provide The Expert
            Service, Service Content, and/or Products on an economic basis
            without such limitations. The warranty disclaimer and limitation of
            liability inure to the benefit of The Expert&apos;s suppliers.
          </LI>
        </OL>
      </LI>
      <LI>
        <Strong>NOTICE TO CALIFORNIA RESIDENTS</Strong>. Pursuant to California
        Civil Code Section 1789.3, The Expert provides users of The Expert
        Service with the following notice: You may report complaints to the
        Consumer Information Division of the California Department of Consumer
        Affairs in writing at 1625 North Market Blvd., Suite N 112, Sacramento,
        CA 95834, or by telephone at 800-952-5210.
      </LI>
      <LI>
        <Strong>AVAILABILITY OF THE EXPERT SERVICE</Strong>. The Expert Service
        is operated by The Expert in the United States from its offices in New
        York, NY and Los Angeles, CA. The Expert Service is intended for users
        who reside in the United States of America. Information describing The
        Expert Service may be accessible worldwide but this does not mean The
        Expert Service, or certain portions thereof, are available in your
        country. If you access and use The Expert Service outside the United
        States, you do so at your own risk and are responsible for compliance
        with applicable local laws. The Expert may restrict access to The Expert
        Service, or portions thereof, in certain countries in its sole
        discretion. It is your responsibility to make sure your use of The
        Expert Service is legal in your country of residence. The Expert Service
        may not be available or accessible in all languages.
      </LI>
      <LI>
        <Strong>GOVERNING LAW AND DISPUTE RESOLUTION</Strong>
        <OL>
          <LI>
            <Strong>Governing Law</Strong>. These Terms shall be governed in all
            respects by and construed in accordance with the laws of the State
            of New York, without regard to its conflicts of laws principles. The
            United Nations Convention on Contracts for the International Sale of
            Goods in its entirety and the Uniform Computer Information
            Transactions Act (UCITA) or any other act derived from or related to
            UCITA are expressly excluded from this Agreement. The Federal
            Arbitration Act and federal arbitration law apply to the agreement
            to arbitrate by the parties set forth in this Section 16.
          </LI>
          <LI>
            Disputes. Except as otherwise set forth in these Terms, you agree
            that any dispute between you and The Expert arising out of or
            relating to these Terms, any Additional Terms, The Expert Service
            (or any part thereof), any Service Content, and/or the purchase of
            any Products (collectively, &ldquo;
            <Strong>Disputes</Strong>&rdquo;) shall be governed by the
            provisions set forth in this Section.
          </LI>
          <LI>
            <Strong>Agreement to Arbitrate</Strong>. In the interest of
            resolving Disputes between you and The Expert in the most expedient
            and cost-effective manner, and except as described in Section 16.7,
            you and The Expert agree that every dispute arising in connection
            with these Terms will be resolved by binding individual (not class)
            arbitration (the &ldquo;
            <Strong>Agreement to Arbitrate</Strong>&rdquo;). Arbitration is less
            formal than a lawsuit in court. Arbitration uses a neutral
            arbitrator instead of a judge or jury, may allow for more limited
            discovery than in court, and can be subject to very limited review
            by courts. Arbitrators can award the same damages and relief that a
            court can award. This Agreement to Arbitrate includes all claims
            arising out of or relating to any aspect of these Terms, whether
            based in contract, tort, statute, fraud, misrepresentation, or any
            other legal theory, and regardless of whether a claim arises during
            or after the termination of these Terms.
            <Strong>
              THIS AGREEMENT TO ARBITRATE MEANS YOU AND THE EXPERT WAIVE
              YOUR/ITS RESPECTIVE RIGHTS TO A JURY TRIAL. THERE IS NO JUDGE OR
              JURY IN ARBITRATION, AND COURT REVIEW OF AN ARBITRATION AWARD IS
              LIMITED. The arbitrator has exclusive authority to resolve any
              dispute relating to the interpretation, applicability, or
              enforceability of the Agreement to Arbitrate
            </Strong>
            .
          </LI>
          <LI>
            <Strong>Notice of Arbitration; Instituting Proceedings</Strong>. A
            party who intends to seek arbitration must first send a written
            notice of the dispute to the other party by certified U.S. Mail or
            by Federal Express (signature required) or, only if that other party
            has not provided a current physical address, then by electronic mail
            (&ldquo;
            <Strong>Notice</Strong>&rdquo;). The Expert&apos;s address for
            Notice is:
            <a href="mailto:hello@theexpert.com">hello@theexpert.com</a>;
            Subject Line: Notice of Dispute. The Notice must: (a) describe the
            nature and basis of the claim or dispute; and (b) set forth the
            specific relief sought (&ldquo;
            <Strong>Demand</Strong>&rdquo;). You and The Expert agree to use
            good faith efforts to resolve the claim directly, but if we do not
            reach an agreement to do so within 60 days after the Notice is
            received by the other party, you or The Expert may commence an
            arbitration proceeding, as set forth above. A party electing
            arbitration must initiate proceedings by filing an arbitration
            demand with the AAA. The AAA Rules and filing forms are available
            online at{' '}
            <a href="https://www.adr.org" target="_blank" rel="noreferrer">
              www.adr.org
            </a>
            , by calling the AAA at 1-800-778-7879.
          </LI>
          <LI>
            <Strong>Arbitration</Strong>. Any arbitration between you and The
            Expert will be administered by the American Arbitration Association
            (&ldquo;
            <Strong>AAA</Strong>&rdquo;) under its Consumer Arbitration Rules
            (collectively, &ldquo;
            <Strong>AAA Rules</Strong>&rdquo;) as modified by this Section 16.5.
            You and we agree that the following rules will apply to the
            proceedings: (a) the arbitration will be heard and determined by a
            single, neutral arbitrator mutually agreed on by you and The Expert
            (provided, if you and The Expert cannot agree within 30 days from
            commencement of the claim, the arbitrator will be selected in
            accordance with the AAA Rules); (b) the arbitration will take place
            at a location within the county you reside or another location
            mutually agreed on by the parties, but will be conducted solely by
            telephone, online, or based solely on written submissions (except as
            set forth below or unless you and we agree otherwise); (c) the
            arbitration must not involve any witnesses (unless we and you agree
            otherwise); and (d) any judgment on the arbitrator&apos;s rendered
            award may be entered in any court with competent jurisdiction.
            Disputes that involve a claim of more than $10,000 USD must be
            resolved per the AAA&apos;s rules about whether the arbitration
            hearing has to be in-person. All arbitration proceedings between the
            parties will be confidential unless otherwise agreed by the parties
            in writing. Regardless of the manner in which the arbitration is
            conducted, the arbitrator must issue a reasoned written decision
            sufficient to explain the essential findings and conclusions on
            which the decision and award, if any, are based. The arbitrator may
            make rulings and resolve disputes as to the payment and
            reimbursement of fees or expenses at any time during the proceeding
            and upon request from either party made within fourteen (14) days of
            the arbitrator&apos;s ruling on the merits.
          </LI>
          <LI>
            <Strong>Arbitration Fees</Strong>. Payment of all filing,
            administration and arbitrator fees will be governed by the
            AAA&apos;s rules. If you commence arbitration in accordance with
            these Terms and your claim in total is less than $10,000 USD, The
            Expert will reimburse the filing fees paid by you, unless the
            arbitrator determines that your claim is frivolous or brought for an
            improper purpose (as measured by the standards set forth in Federal
            Rule of Civil Procedure 11(b)). Likewise, The Expert will not seek
            attorneys&apos; fees and costs in arbitration unless the arbitrator
            determines the claim is frivolous.
          </LI>
          <LI>
            Exceptions to Arbitration. If you have in any manner violated or
            threatened to violate any of The Expert&apos;s intellectual property
            rights, The Expert may seek injunctive or other appropriate relief
            in any state or federal court with competent jurisdiction in any
            country, including in the State of New York, United States of
            America, without first engaging in arbitration or the informal
            dispute process set forth in this Section, and you hereby consent to
            the personal jurisdiction and exclusive venue in such courts. In
            addition, despite the Agreement to Arbitrate, nothing in these Terms
            will be deemed to waive, preclude, or otherwise limit the right of
            either party to: (a) bring an individual action in small claims
            court; (b) pursue an enforcement action through the applicable
            federal, state, or local agency if that action is available; or (c)
            seek injunctive relief in a court of law in aid of arbitration.
          </LI>
          <LI>
            <Strong>Opting-Out of Arbitration</Strong>. If you do not wish to
            resolve Disputes by binding arbitration, you may opt out of the
            Agreement to Arbitrate within 30 days after the date that you agree
            to these Terms by sending an opt-out notice via email to us at:
            <a href="mailto:hello@theexpert.com">hello@theexpert.com</a>;
            Subject Line: Arbitration Opt-Out Notice, that specifies: your full
            legal name, the email address used to register an Account (if
            applicable), and a statement that you wish to opt out of arbitration
            (&ldquo;
            <Strong>Opt-Out Notice</Strong>&rdquo;). Once The Expert receives
            your Opt-Out Notice, the Agreement to Arbitrate will be void and the
            parties agree that the exclusive jurisdiction and venue described in
            Section 16.10 will govern any action arising out of or related to
            these Terms. The remaining provisions of this Section 16 will not be
            affected by your Opt-Out Notice.
          </LI>
          <LI>
            <Strong>No Class Actions</Strong>. YOU MAY ONLY RESOLVE DISPUTES
            WITH THE EXPERT ON AN INDIVIDUAL BASIS, AND MAY NOT BRING A CLAIM AS
            A PLAINTIFF OR A CLASS MEMBER IN A CLASS, CONSOLIDATED, OR
            REPRESENTATIVE ACTION. CLASS ARBITRATIONS, CLASS ACTIONS, PRIVATE
            ATTORNEY GENERAL ACTIONS, AND CONSOLIDATION WITH OTHER ARBITRATIONS
            AREN&apos;T ALLOWED UNDER THESE TERMS.
          </LI>
          <LI>
            <Strong>Enforceability; Venue</Strong>. If the Agreement to
            Arbitrate is found not to apply to your or our claim, you and The
            Expert agree that any judicial proceeding will be brought in the
            federal or state courts located in New York, New York. Both you and
            The Expert consent to venue and personal jurisdiction there.
          </LI>
          <LI>
            <Strong>Time Limitation to Bring Claims</Strong>. Notwithstanding
            any statute or law to the contrary, any claim or cause of action
            arising out of or related to your use of The Expert Service (or any
            part thereof), Service Content, and/or Products provided or made
            available by The Expert hereunder must be filed within one (1) year
            after such claim or cause of action arose, otherwise that claim or
            cause of action will be barred forever.
          </LI>
        </OL>
      </LI>
      <LI>
        <Strong>GENERAL</Strong>. Neither the rights nor the obligations arising
        under these Terms and/or any Additional Terms are assignable by you, and
        any such attempted assignment or transfer shall be void and without
        effect. Any waiver of any provision of these Terms and/or Additional
        Terms must be in writing and executed by both parties. The failure of
        either party to exercise any right provided for by these Terms and/or
        any Additional Terms shall not be deemed a waiver of that right. If any
        term or provision of these Terms and/or any Additional Terms is
        determined to be illegal, unenforceable, or invalid in whole or in part
        for any reason, that provision shall be stricken from these Terms and/or
        any Additional Terms (as applicable) and shall not affect the legality,
        enforceability or validity of the remainder of the provisions set forth
        in these Terms and/or any Additional Terms (as applicable). The parties
        acknowledge and agree that they are dealing with each other as
        independent contractors and nothing in these Terms and/or any Additional
        Terms and/or its performance shall be construed as creating a joint
        venture or agency between The Expert and you. The Expert may delegate
        the performance of any services hereunder to its affiliates and
        contractors. These Terms, the Additional Terms, including, but not
        limited to, the{' '}
        <a
          href="https://www.theexpert.com/privacy"
          target="_blank"
          rel="noreferrer"
        >
          Privacy Notice
        </a>
        ,{' '}
        <a
          href="https://www.theexpert.com/blog/purchase-terms-and-conditions"
          target="_blank"
          rel="noreferrer"
        >
          Purchase Terms and Conditions
        </a>
        ,{' '}
        <a
          href="https://www.theexpert.com/blog/marketplace-return-policy"
          target="_blank"
          rel="noreferrer"
        >
          Return Policy
        </a>
        ,{' '}
        <a
          href="https://www.theexpert.com/blog/text-service-terms-and-conditions"
          target="_blank"
          rel="noreferrer"
        >
          Text Service T&Cs
        </a>
        ,{' '}
        <a
          href="https://www.theexpert.com/blog/infringement-policy"
          target="_blank"
          rel="noreferrer"
        >
          Infringement Policy
        </a>
        , and any other policies or terms and condition referenced herein,
        constitute the entire agreement between the parties regarding the
        subject matter, and supersedes all prior oral or written agreements or
        communications with regard to the subject matter described. Any notice
        to you may be provided by email. The headings of Sections of these Terms
        and the Additional Terms are for convenience and are not to be used in
        interpreting these Terms and/or the Additional Terms (as applicable). As
        used in these Terms and any Additional Terms, the word “including” means
        “including but not limited to.” You agree that neither these Terms nor
        any Additional Terms will be construed against The Expert by virtue of
        having drafted them. The official text of these Terms and the Additional
        Terms (and any notice submitted hereunder or thereunder) will be in
        English. The parties acknowledge that they require that these Terms and
        the Additional Terms be drawn up in the English language only. In the
        event of any dispute concerning the construction or meaning of these
        Terms and/or any Additional Terms, reference will be made only to these
        Terms and/or any Additional Terms (as applicable) as written in English
        and not to any translation into another language. Any delay in
        performance of any duties or obligations of either party will not be
        considered a breach of these Terms if such delay is caused by a labor
        dispute, shortage of materials, fire, earthquake, flood,
        telecommunications or Internet failure, plague, epidemic, pandemic,
        outbreaks of infectious disease or any other public health crisis,
        including quarantine or other employee restrictions, or any other event
        beyond the reasonable control of such party.
      </LI>
      <LI>
        <Strong>QUESTIONS AND ADDITIONAL INFORMATION</Strong>. Please feel free
        to contact us at{' '}
        <a href="mailto:hello@theexpert.com">hello@theexpert.com</a> if you have
        any questions about these Terms.
      </LI>
    </OL>
    <PureSSRIndicator />
  </Box>
)

export function getStaticProps() {
  return prepareGetStaticNavigationProps()
}

TermsOfUse.getLayout = (page) =>
  getPublicLayout(page, { isWithBreadcrumbs: true })
